import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {InputDataFrames} from 'pipelines/StepDataUtils';
import {getStepTypeConfigShape, isValueValid} from 'pipelines/StepShapeUtils';
import {JSONValue} from 'util/Types';

import {getAdditionalConfigValidationFn} from './additional-validation-functions/AdditionalValidationFunctions';

/**
 * Validate a step's config.
 *
 * This automatically validates, for any step type, what can be checked based on the step type's config shape.
 *
 * This out-of-the-box validation includes:
 * • Required values are non-null
 * • Numeric values are in range
 * • List values have the correct number of items
 * • Column references exist in the input data frame
 * • Enum values are expected
 *
 * If additional validaiton is required for a step type, or user-facing messages are required, register an
 * additional validation function in `AdditionalValidationFunctions.ts`.
 *
 * User-facing `errors` are provided only for additional validation. See `AdditionalValidationFunctions.ts`.
 *
 * @returns {isComplete} True if the config is complete, according to the step config shape. There may be
 *                       `errors` even if the config is complete.
 * @returns {errors} Error messages. Most errors are only generated if the config is complete.
 */
export function validatePipelineStepConfig<StepType extends PipelineStepType, StepVersion extends number>(
  stepType: StepType,
  stepVersion: StepVersion,
  config: Record<string, JSONValue>,
  inputDataFrames: InputDataFrames<StepType, StepVersion>
): {isComplete: boolean; errors: Array<string>} {
  const {validateConfig, validateDraftConfig} = getAdditionalConfigValidationFn(stepType, stepVersion);

  const errors: Array<string> = [];
  // `validateDraftConfig` supports configs in draft state, which may have intermediate values
  // (e.g. null for required string fields)
  errors.push(
    ...validateDraftConfig(config as PipelineStepConfig<StepType, StepVersion, 'draft'>, inputDataFrames)
  );
  const isComplete = isValueValid(config, getStepTypeConfigShape(stepType, stepVersion), inputDataFrames);
  if (isComplete) {
    // `validateConfig` only works when configs are fully filled out, e.g. no intermediate values
    errors.push(...validateConfig(config as PipelineStepConfig<StepType, StepVersion>, inputDataFrames));
  }

  return {isComplete, errors};
}

/**
 * Validate a step's config, for a single-input step type.
 *
 * This assuemes the single input step data is called 'Input dataset'.
 *
 * See `validatePipelineStepConfig`.
 */
export function validateSingleInputPipelineStepConfig(
  stepType: PipelineStepType,
  stepVersion: number,
  config: Record<string, JSONValue>,
  inputDataFrameSpec: DataFrameSpec
): ReturnType<typeof validatePipelineStepConfig> {
  return validatePipelineStepConfig(stepType, stepVersion, config, {
    [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [inputDataFrameSpec],
  });
}
